/**
 * carousel.js — Embla carousel bootstrap (replaces embla-carousel-react).
 * Vendored embla UMD builds expose window.EmblaCarousel / window.EmblaCarouselAutoplay.
 *
 * Markup contract:
 *   <div data-carousel='{"loop": true, "align": "start"}' data-carousel-autoplay="4000">
 *     <div data-carousel-viewport class="overflow-hidden">
 *       <div class="flex">…slides…</div>
 *     </div>
 *     <button data-carousel-prev>…</button>
 *     <button data-carousel-next>…</button>
 *     <div data-carousel-dots></div>            (optional)
 *   </div>
 */
(function () {
  "use strict";

  function initCarousel(root) {
    if (root.__embla || typeof window.EmblaCarousel !== "function") return;
    const viewport = root.querySelector("[data-carousel-viewport]") || root;
    let options = {};
    try {
      options = JSON.parse(root.getAttribute("data-carousel") || "{}");
    } catch (e) {
      options = {};
    }

    const plugins = [];
    const autoplayDelay = root.getAttribute("data-carousel-autoplay");
    // Respect the merchant "Enable animations" toggle and the OS reduce-motion
    // preference — manual navigation still works, only autoplay is suppressed.
    const motionOk =
      (!window.themeConfig || window.themeConfig.animationsEnabled !== false) &&
      !(
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      );
    if (autoplayDelay && motionOk && typeof window.EmblaCarouselAutoplay === "function") {
      plugins.push(
        window.EmblaCarouselAutoplay({
          delay: Number(autoplayDelay) || 4000,
          stopOnInteraction: false,
        }),
      );
    }

    const embla = window.EmblaCarousel(viewport, options, plugins);
    root.__embla = embla;

    const prev = root.querySelector("[data-carousel-prev]");
    const next = root.querySelector("[data-carousel-next]");
    if (prev) prev.addEventListener("click", () => embla.scrollPrev());
    if (next) next.addEventListener("click", () => embla.scrollNext());

    function syncButtons() {
      if (prev) prev.toggleAttribute("disabled", !embla.canScrollPrev());
      if (next) next.toggleAttribute("disabled", !embla.canScrollNext());
    }

    const dotsHolder = root.querySelector("[data-carousel-dots]");
    let dots = [];
    function buildDots() {
      if (!dotsHolder) return;
      dotsHolder.innerHTML = "";
      dots = embla.scrollSnapList().map((_, index) => {
        const dot = document.createElement("button");
        dot.type = "button";
        dot.className =
          "h-2 w-2 rounded-full bg-primary/30 transition-all duration-200";
        dot.setAttribute("aria-label", "Go to slide " + (index + 1));
        dot.addEventListener("click", () => embla.scrollTo(index));
        dotsHolder.appendChild(dot);
        return dot;
      });
    }
    function syncDots() {
      const selected = embla.selectedScrollSnap();
      dots.forEach((dot, index) => {
        dot.classList.toggle("bg-primary", index === selected);
        dot.classList.toggle("w-4", index === selected);
        dot.classList.toggle("bg-primary/30", index !== selected);
      });
    }

    buildDots();
    syncButtons();
    syncDots();
    embla.on("select", () => {
      syncButtons();
      syncDots();
      // Let listeners (e.g. video.js click-to-play) react to slide changes.
      root.dispatchEvent(new CustomEvent("carousel:select", { bubbles: true }));
    });
    embla.on("reInit", () => {
      buildDots();
      syncButtons();
      syncDots();
    });
  }

  function scan(root) {
    (root || document).querySelectorAll("[data-carousel]").forEach(initCarousel);
  }

  window.themeUtils.onReady(() => scan());
  document.addEventListener("carousel:scan", (e) => scan(e.target));
  window.themeCarousels = { scan: scan };
})();
